<template>
    <div class="order-note-template">
        <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
                Order Note Template
                <base-question-icon
                    v-if="tooltip"
                    v-tooltip="tooltip"
                    class="ml-1 h-4 w-4 inline-block text-gray-400 cursor-help"
                />
            </label>
            <p class="text-xs text-gray-500 mb-3">
                Customize the order notes sent to your POS system. Use variables
                like {{ exampleVariables }}, etc. Click the "Insert
                Variable" button to see all available options.
            </p>
        </div>

        <div class="border border-gray-300 rounded-md overflow-hidden bg-white">
            <editor-content
                :editor="editor"
                class="px-4 py-3 min-h-[120px] prose prose-sm max-w-none"
            />

            <div
                class="flex items-center justify-between bg-gray-50 px-4 py-2 border-t border-gray-200"
            >
                <div class="flex items-center space-x-2">
                    <button
                        type="button"
                        @click="showVariableSelector = !showVariableSelector"
                        class="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <base-plus-icon class="h-3 w-3 mr-1" />
                        Insert Variable
                    </button>

                    <button
                        v-if="showPreview"
                        type="button"
                        @click="togglePreview"
                        class="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <base-eye-icon class="h-3 w-3 mr-1" />
                        {{ previewMode ? 'Edit' : 'Preview' }}
                    </button>
                </div>

                <div class="flex items-center space-x-2">
                    <span
                        v-if="validationError"
                        class="text-xs text-red-600 flex items-center"
                    >
                        <base-warning-icon class="mr-1 h-4 w-4" />
                        {{ validationError }}
                    </span>
                    <span
                        v-else-if="characterCount > 0"
                        class="text-xs text-gray-500"
                    >
                        ~{{ characterCount }} characters (estimated)
                    </span>
                </div>
            </div>
        </div>

        <transition name="fade">
            <div
                v-if="showVariableSelector"
                class="mt-3 border border-gray-200 rounded-md bg-white shadow-sm"
            >
                <div class="px-4 py-3 border-b border-gray-200">
                    <h4 class="text-sm font-medium text-gray-900">
                        Available Variables
                    </h4>
                    <p class="text-xs text-gray-500 mt-1">
                        Click a variable to insert it at the cursor position
                    </p>
                </div>

                <div class="px-4 py-3 max-h-96 overflow-y-auto">
                    <div
                        v-for="category in groupedVariables"
                        :key="category.name"
                        class="mb-4 last:mb-0"
                    >
                        <h5
                            class="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2"
                        >
                            {{ category.name }}
                        </h5>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <button
                                v-for="variable in category.variables"
                                :key="variable.name"
                                type="button"
                                @click="insertVariable(variable.name)"
                                class="text-left px-3 py-2 border border-gray-200 rounded hover:bg-gray-50 hover:border-indigo-300 transition-colors"
                            >
                                <div class="flex items-start">
                                    <code
                                        class="text-xs font-mono text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded"
                                    >
                                        {{ variableDisplay(variable.name) }}
                                    </code>
                                </div>
                                <p class="text-xs text-gray-600 mt-1">
                                    {{ variable.description }}
                                </p>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <transition name="fade">
            <div
                v-if="previewMode && previewText"
                class="mt-3 border border-gray-200 rounded-md bg-gray-50 p-4"
            >
                <h4 class="text-sm font-medium text-gray-900 mb-2">
                    Preview (with sample data)
                </h4>
                <div
                    class="text-sm text-gray-700 bg-white p-3 rounded border border-gray-200"
                >
                    {{ previewText }}
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extensions';

export default {
    name: 'OrderNoteTemplate',

    components: {
        EditorContent
    },

    props: {
        value: {
            type: String,
            default: ''
        },
        maxLength: {
            type: Number,
            default: null
        },
        tooltip: {
            type: String,
            default: ''
        },
        showPreview: {
            type: Boolean,
            default: true
        },
        placeholder: {
            type: String,
            default: '{{customerNotes}}'
        }
    },

    data() {
        return {
            editor: null,
            showVariableSelector: false,
            previewMode: false,
            variables: [],
            isLoadingVariables: false,
            sampleData: {
                firstName: 'John',
                lastName: 'Doe',
                fullName: 'John Doe',
                email: 'john.doe@example.com',
                phone: '+44 7123 456789',
                customerNotes: 'Please ring the doorbell',
                orderId: '12345',
                orderCode: 'ABC123',
                orderTotal: '25.50',
                orderSubtotal: '22.00',
                deliveryFee: '2.50',
                serviceCharge: '1.00',
                tip: '2.00',
                discountAmount: '0.00',
                itemCount: '3',
                paymentType: 'Card',
                tableNumber: 'Table 5',
                tableName: 'Table 5',
                tablePosId: 'T5',
                deliveryStreet: 'High Street',
                deliveryBuildingNumber: '123',
                deliveryCity: 'London',
                deliveryPostCode: 'SW1A 1AA',
                deliveryCountry: 'UK',
                deliveryNotes: 'Leave at door',
                deliveryFullAddress: '123, High Street, London, SW1A 1AA',
                fulfillmentMethod: 'DELIVERY',
                isPickup: 'No',
                isDelivery: 'Yes',
                isInStore: 'No',
                estimatedTime: '18:30',
                preOrderDateTime: '19:00',
                venueName: 'Sample Restaurant',
                venuePhone: '+44 20 1234 5678',
                noOfDiners: '2'
            }
        };
    },

    computed: {
        exampleVariables() {
            return '{{firstName}}, {{orderCode}}';
        },

        characterCount() {
            if (!this.editor) return 0;

            // Get the raw text with template variables
            const text = this.editor.getText();

            // Count static characters (non-variable text)
            const staticText = text.replace(/\{\{\w+\}\}/g, '');
            const staticLength = staticText.length;

            // Count variables and estimate their expanded length
            const variablePattern = /\{\{(\w+)\}\}/g;
            const matches = [...text.matchAll(variablePattern)];

            // Estimate average variable length based on common values
            // Most variables expand to 10-30 characters (names, codes, addresses)
            const estimatedVariableLength = 20;
            const totalEstimatedVariableLength = matches.length * estimatedVariableLength;

            return staticLength + totalEstimatedVariableLength;
        },

        validationError() {
            if (this.maxLength && this.characterCount > this.maxLength) {
                return `Exceeds maximum length of ${this.maxLength} characters`;
            }

            const text = this.editor ? this.editor.getText() : '';
            const variablePattern = /\{\{(\w+)\}\}/g;
            const matches = [...text.matchAll(variablePattern)];

            for (const match of matches) {
                const variableName = match[1];
                const isValid = this.variables.some(
                    v => v.name === variableName
                );
                if (!isValid) {
                    return `Unknown variable: {{${variableName}}}`;
                }
            }

            return null;
        },

        groupedVariables() {
            const groups = {};

            this.variables.forEach(variable => {
                if (!groups[variable.category]) {
                    groups[variable.category] = {
                        name: variable.category,
                        variables: []
                    };
                }
                groups[variable.category].variables.push(variable);
            });

            return Object.values(groups);
        },

        previewText() {
            if (!this.editor) return '';

            let text = this.editor.getText();

            Object.keys(this.sampleData).forEach(key => {
                const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
                text = text.replace(regex, this.sampleData[key]);
            });

            return text;
        }
    },

    watch: {
        value(newValue) {
            if (this.editor && this.editor.getText() !== newValue) {
                this.editor.commands.setContent(newValue, false);
            }
        }
    },

    async mounted() {
        await this.fetchVariables();

        this.editor = new Editor({
            content: this.value,
            extensions: [
                StarterKit.configure({
                    heading: false,
                    codeBlock: false,
                    blockquote: false,
                    horizontalRule: false,
                    bulletList: false,
                    orderedList: false,
                    listItem: false
                }),
                Placeholder.configure({
                    placeholder: this.placeholder
                })
            ],
            editorProps: {
                attributes: {
                    class: 'focus:outline-none'
                }
            },
            onUpdate: () => {
                const text = this.editor.getText();
                this.$emit('input', text);
                this.$emit('validation-change', !this.validationError);
            },
            onFocus: () => {
                // Initialize with {{customerNotes}} if empty when clicked into
                if (this.editor && this.editor.isEmpty) {
                    this.editor.commands.setContent('{{customerNotes}}');
                }
            }
        });
    },

    beforeDestroy() {
        if (this.editor) {
            this.editor.destroy();
        }
    },

    methods: {
        async fetchVariables() {
            this.isLoadingVariables = true;
            try {
                const { data } = await this.$axios.get(
                    '/venues/order-note-template-variables'
                );
                this.variables = data;
            } catch (error) {
                console.error('Failed to fetch template variables:', error);
                this.variables = [];
            } finally {
                this.isLoadingVariables = false;
            }
        },

        insertVariable(variableName) {
            if (this.editor) {
                this.editor
                    .chain()
                    .focus()
                    .insertContent(`{{${variableName}}}`)
                    .run();
                this.showVariableSelector = false;
            }
        },

        togglePreview() {
            this.previewMode = !this.previewMode;
        },

        clear() {
            if (this.editor) {
                this.editor.commands.clearContent();
            }
        },

        focus() {
            if (this.editor) {
                this.editor.commands.focus();
            }
        },

        variableDisplay(name) {
            return `{{${name}}}`;
        }
    }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.order-note-template >>> .ProseMirror {
    min-height: 120px;
}

.order-note-template >>> .ProseMirror p.is-editor-empty:first-child::before {
    color: #9ca3af;
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
}

.order-note-template >>> .ProseMirror:focus {
    outline: none;
}
</style>
